import APFormGroup from 'components/FormFields/APFormGroup';
import FormSelect from 'components/FormFields/FormSelect';
import InputField from 'components/FormFields/InputField';
import NotificationPreferences from 'components/SettingsGrid/NotificationPreferences';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { TIME_ZONES, TIME_ZONES_ORDER } from 'utils/constants';

const COMPANY_NOTIFICATIONS_CATEGORIES = [
  {
    name: 'COMPANY ACCOUNT ACCESS',
    prefs: [
      {
        name: 'Team Member Added',
        key: 'org.member-added',
      },
      {
        name: "You've Been Added",
        key: 'org.added-you',
      },
    ],
  },
  {
    name: 'LEADS',
    prefs: [
      {
        name: 'New Lead (If On In Lead Settings)',
        key: 'lead.inquiry.created',
        disabled: true,
      },
      {
        name: 'Lead Assignments',
        key: 'lead.assignment.created',
      },
    ],
  },
  {
    name: 'QUOTES',
    prefs: [
      {
        name: 'Quote Accepted',
        key: 'org.quote.accepted',
      },
    ],
  },
  {
    name: 'INVOICES (IF SENT BY YOU)',
    prefs: [
      {
        name: 'Payment Past Due',
        key: 'org.invoice.payment.past_due',
      },
      {
        name: 'Payment Received',
        key: 'org.invoice.payment',
      },
    ],
  },
  {
    name: 'PROPOSALS & CONTRACTS',
    prefs: [
      {
        name: 'Contract Ready To Be Countersigned',
        key: 'lead.document.countersign',
      },
      {
        name: 'All Signatures Completed',
        key: 'lead.document.accepted',
      },
    ],
  },
  {
    name: 'QUESTIONNAIRES',
    prefs: [
      {
        name: 'Questionnaire Submitted',
        key: 'document.questionnaire.submit',
      },
    ],
  },
];

const PROJECT_NOTIFICATIONS_CATEGORIES = [
  {
    name: 'PROJECT ACCESS',
    prefs: [
      {
        name: 'Planning Partner Added',
        key: 'wedding.partner-added',
      },
      {
        name: 'You Are Added',
        key: 'wedding.added-you',
      },
    ],
  },
  {
    name: 'COMMENTS',
    prefs: [
      {
        name: 'On Tasks',
        key: 'comment.task',
      },
      {
        name: 'On Images',
        key: 'comment.style-guide-image',
      },
      {
        name: 'On Project Contacts',
        key: 'comment.vendor-file-area',
      },
    ],
  },
  {
    name: "TASKS I'M ASSIGNED TO",
    prefs: [
      {
        name: 'Assignment',
        key: 'task.assigned',
      },
      {
        name: 'Reminders',
        key: 'reminder.task',
      },
      {
        name: 'Completed (By Someone Else)',
        key: 'task.completed',
      },
    ],
  },
  {
    name: "TASKS I'VE ASSIGNED OUT",
    prefs: [
      {
        name: 'Reminders',
        key: 'reminder.task.involved',
      },
      {
        name: 'Completed',
        key: 'task.completed.involved',
      },
    ],
  },
  {
    name: "ALL TASKS (IF I'M THE PROJECT LEAD)",
    prefs: [
      {
        name: 'Reminders',
        key: 'reminder.task.broadcast',
      },
      {
        name: 'Completed',
        key: 'task.completed.broadcast',
      },
    ],
  },
  {
    name: 'OTHER NOTIFICATIONS',
    prefs: [
      {
        name: 'Payment Reminders',
        key: 'reminder.wedding-vendor-payment',
      },
    ],
  },
];

const NOTIFICATIONS_EMAIL_KIND = [
  { value: 'delayed', title: 'As they happen, grouped with others near the same time', default: true },
  { value: 'daily', title: 'Daily' },
];

const TIME_FORMATS = ['AM', 'PM'];

const AccountSettings = ({
  getNotificationPreferences,
  saveNotificationPreferences,
}) => {
  const notificationPreferences = useSelector((state) => state.global.notificationPreferences);
  const defaultEmailBatch = useSelector((state) => state.global.defaultEmailBatch);

  const [notificationEmailKind, setNotificationEmailKind] = useState(null);
  const [timeFormat, setTimeFormat] = useState(null);
  const [time, setTime] = useState(null);
  const [time24, setTime24] = useState(null);
  const [timeZone, setTimeZone] = useState(null);

  useEffect(() => {
    getNotificationPreferences();
  }, []);

  useEffect(() => {
    if (defaultEmailBatch) {
      if (defaultEmailBatch.daySendTime) {
        const time12 = timeToFormat12(defaultEmailBatch.daySendTime);
        setTime(time12.time);
        setTimeFormat(time12.timeFormat);
        setTime24(defaultEmailBatch.daySendTime);
      } else {
        setTime('08:00');
        setTime24('08:00');
        setTimeFormat(TIME_FORMATS[0]);
      }
      if (defaultEmailBatch.daySendTz) {
        setTimeZone(defaultEmailBatch.daySendTz);
      }
      if (defaultEmailBatch.kind) {
        setNotificationEmailKind(defaultEmailBatch.kind);
      }
   }
  }, [defaultEmailBatch]);

  useEffect(() => {
    if (defaultEmailBatch
      && notificationEmailKind
      && (time24 !== defaultEmailBatch.daySendTime
        || timeZone !== defaultEmailBatch.daySendTz
        || notificationEmailKind !== defaultEmailBatch.kind
        )
      && ((timeFormat && time24 && timeZone) || notificationEmailKind === 'delayed')) {
      saveEmailBatch({ ...defaultEmailBatch, daySendTime: time24, daySendTz: timeZone, kind: notificationEmailKind });
    }
  }, [time24, timeZone, notificationEmailKind]);

  useEffect(() => {
    setTime24(timeToFormat24(time));
  }, [time, timeFormat]);

  const timeToFormat12 = (timeStr) => {
    const items = timeStr.split(':');
    if (items.length >= 2) {
      const hours = Number(items[0]);
      const minutes = items[1];
      if (hours > 12) {
        if(hours - 12 > 9){
          return { time: `${hours - 12}:${minutes}`, timeFormat: TIME_FORMATS[1] };
        }
        return { time: `0${hours - 12}:${minutes}`, timeFormat: TIME_FORMATS[1] };
      }
    }
    return { time: timeStr, timeFormat: TIME_FORMATS[0] };
  };

  const timeToFormat24 = (timeStr) => {
    if (timeFormat === TIME_FORMATS[1]) {
      const items = timeStr.split(':');
      if (items.length >= 2) {
        const hours = Number(items[0]);
        const minutes = items[1];
        if (hours === 12) {
          return `00:${minutes}`;
        }
        return `${hours + 12}:${minutes}`;
      }
    }
    return timeStr;
  };

  const savePreferences = (prefs) => {
    saveNotificationPreferences({ prefs, defaultEmailBatch });
  };

  const saveEmailBatch = (emailBatch) => {
    saveNotificationPreferences({ prefs: notificationPreferences, defaultEmailBatch: emailBatch });
  };

  const introCompanyText = (
    <>
      If you are a member of a company in Aisle Planner, you can manage your
      preferences for company related notifications below. Learn more about
      setting your company notifications {' '}
      <a
        href="https://help.aisleplanner.com/en/articles/2739790-what-notifications-are-available#h_3e7b3a655b"
        target="_blank"
        rel="noreferrer"
      >
        here
      </a>.
    </>
  );
  const introProjectText = (
    <>
      Manage your default project notifications for all projects below. Once
      you've created or have been added to a new project, we'll use these
      defaults to set your notification preferences. You can always adjust your
      notifications within each project. Learn more about personalizing your
      project notifications {' '}
      <a
        href="https://help.aisleplanner.com/en/articles/2739790-what-notifications-are-available#h_84c6bc9aa3"
        target="_blank"
        rel="noreferrer"
      >
        here
      </a>.
    </>
  );

  return (
    <div className='settings-container'>
      <div className="account-title">Notifications</div>
      <p>
        Notifications help you keep tabs on what's happening in your account so
        you're always in the loop. Learn more about the basics of in-app and
        emailed notifications{' '}
        <a
          href="https://help.aisleplanner.com/en/articles/2739654-how-notifications-work"
          target="_blank"
          rel="noreferrer"
        >
          here
        </a>.
      </p>
      <NotificationPreferences
        introText={introProjectText}
        title="Project Notifications"
        categories={PROJECT_NOTIFICATIONS_CATEGORIES}
        items={notificationPreferences}
        savePreferences={savePreferences}
      />
      <NotificationPreferences
        introText={introCompanyText}
        title="Company Notifications"
        categories={COMPANY_NOTIFICATIONS_CATEGORIES}
        items={notificationPreferences}
        savePreferences={savePreferences}
      />
      <APFormGroup
        title="Email Batching"
        grid={false}
      >
        <FormSelect
          name="Send Notification Emails"
          value={notificationEmailKind}
          onChange={(e) => setNotificationEmailKind(e.target.value)}
        >
          {NOTIFICATIONS_EMAIL_KIND.map((kind, index) => (
            <option
              value={kind.value}
              key={`kind_${kind.value}_${index}`}
            >
              {kind.title}
            </option>
          ))}
        </FormSelect>
        {notificationEmailKind === 'daily' && (
          <>
            <span className='form-item__label'>at</span>
            <APFormGroup grid>
              <InputField
                name="Time"
                showLabel={false}
                value={time}
                onChange={(e) => setTime(e.target.value)}
                type="time"
              />
              <FormSelect
                name="Time Format"
                wrapperProps={{ labelHidden: true }}
                value={timeFormat}
                onChange={(e) => setTimeFormat(e.target.value)}
              >
                {TIME_FORMATS.map((format) => (
                  <option
                    value={format}
                    key={`format_${format}`}
                  >
                    {format}
                  </option>
                ))}
              </FormSelect>
              <FormSelect
                name="Time Zone"
                wrapperProps={{ labelHidden: true }}
                value={timeZone}
                onChange={(e) => setTimeZone(e.target.value)}
                showSearch
              >
                {TIME_ZONES_ORDER.map((option, index) => (
                  <optgroup
                    label={option.value}
                    key={`option_${index}`}
                  >
                    {TIME_ZONES.filter((tz) => tz.group === option.value).map(
                      (optionTZ, indexTZ) => (
                        <option
                          value={optionTZ.value}
                          key={`optionTZ_${option.value}_${indexTZ}`}
                        >
                          {optionTZ.label}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </FormSelect>
            </APFormGroup>
          </>
        )}
      </APFormGroup>
    </div>
  );
};

export default AccountSettings;
